/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/components/master-data/MasterDataCrudPage.tsx
 * PURPOSE: Generic, config-driven master-data CRUD page — `<MasterDataCrudPage
 *          schema={entitySchema} />` gives any master-data entity a full
 *          Create/Read/Update/Delete screen for free, built on the existing
 *          generic DataTable.tsx (BR-PLAT-001-R1/R3).
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-09-02 (issue #715 fix — create/update/delete error banners;
 *       textarea FieldType wired 2026-07-10 per DD-0080)
 * COMPLIANCE: SDLC 6.5.0 + BR-PLAT-001-MASTER-DATA-CRUD-IMPORT-EXPORT-STANDARD
 * PILLAR: P1 (Stage 06 - BUILD) + P6 (UI/UX Design)
 * SPRINT GOVERNANCE: Rule 4: Tier-3 Code Review
 *
 * @business-context
 * - Domain: Cross-Domain Platform Standard — generic master-data CRUD UI
 * - Impact: Framework prerequisite (BR-PLAT-001 AC-2). Replaces per-tenant
 *   hardcoded onboarding scripts (BKL CSV importer, Kupid seed scripts) with
 *   a reusable, tenant-self-service pattern once entities wire a schema in.
 * - Stakeholders: @cto, @architect, entity feature teams (Employee/Org/TenantUser)
 *
 * @technical-architecture
 * - Layer: Frontend Presentation Layer, Shared Component Library
 * - Pattern: Config-Driven UI, Container Component (data-fetching + mutations
 *   + modal state) delegating row rendering to DataTable.tsx
 * - Dependencies: React 18, @tanstack/react-query, frontend/src/services/api/client.ts
 *   (apiClient — the app's shared axios instance, NOT the mobile app's client),
 *   frontend/src/store/auth.ts (useAuthStore — current user role), CSS Modules
 *
 * @cross-references
 * - BR: docs/01-planning/01-requirements/BR-PLAT-001-MASTER-DATA-CRUD-IMPORT-EXPORT-STANDARD.md
 * - Schema contract: frontend/src/components/master-data/types.ts
 * - Table: frontend/src/components/dashboard/DataTable.tsx
 * - Interaction-pattern reference (NOT its inline-table implementation):
 *   frontend/src/pages/OrganizationManagement/index.tsx
 * - Styles: frontend/src/components/master-data/MasterDataCrudPage.module.css
 * - Design Decision: docs/02-design/18-adrs/06-decisions/DD-0080-master-data-framework-gap-resolutions.md
 *   (gap #3 — 'textarea' FieldType — implemented in FormFieldInput below)
 * - Issue: GitHub #715 (BOT-004(a), P1) — create/update/delete mutations had
 *   no onError and nothing rendered `.isError` — a failed write produced
 *   zero user-visible feedback across EVERY entity built on this framework
 *   (EmployeeManagement, JobFamilies, PayGrades). Fixed via `mutationError`/
 *   `submitError` state + `styles.errorBanner`.
 * - Test: frontend/tests/components/master-data/__tests__/MasterDataCrudPage.test.tsx
 *
 * Wired entities: Employee (PR #417, CRUD only) and JobFamily/PayGrade
 * (BR-COMP-002 §8, CRUD + Excel import/export).
 *
 * IMPORT/EXPORT STATUS (updated by BR-COMP-002, the first entity to need it):
 * previously the import/export buttons rendered but their click-handlers were
 * explicit TODO stubs, pending the backend Excel service. That service now has
 * a real caller, so the handlers below are FULLY IMPLEMENTED and generic —
 * an entity gets working Excel import/export by setting
 * `importExport: { enabled: true, ... }` and exposing
 * `${apiBasePath}/import` + `${apiBasePath}/export` on its backend router.
 */

import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DataTable, ColumnDef } from '@/components/dashboard/DataTable';
import { apiClient } from '@/services/api/client';
import { useAuthStore } from '@/store/auth';
import type { EntitySchema, FieldDef, ImportResultSummary } from './types';
import styles from './MasterDataCrudPage.module.css';

export interface MasterDataCrudPageProps<T = any> {
  schema: EntitySchema<T>;
}

/**
 * Normalizes a list response into an array regardless of whether the backend
 * returns a bare array or a paginated `{ items: [...] }` / `{ data: [...] }`
 * envelope. Generic framework code cannot assume a specific per-entity shape.
 */
function normalizeListResponse<T>(payload: unknown): T[] {
  if (Array.isArray(payload)) return payload as T[];
  if (payload && typeof payload === 'object') {
    const obj = payload as Record<string, unknown>;
    if (Array.isArray(obj.items)) return obj.items as T[];
    if (Array.isArray(obj.data)) return obj.data as T[];
  }
  return [];
}

/**
 * Role check matching the same case-insensitive convention used by
 * frontend/src/components/auth/RequireRole.tsx — the app's canonical
 * "is current user allowed" check against useAuthStore().userRole.
 */
function hasAllowedRole(userRole: string | null | undefined, allowed: string[]): boolean {
  if (!allowed || allowed.length === 0) return false;
  const normalized = (userRole || '').toUpperCase();
  return allowed.some((r) => r.toUpperCase() === normalized);
}

function getRowId<T>(row: T, idField: string): string {
  const value = (row as any)[idField];
  return value !== undefined && value !== null ? String(value) : '';
}

export function MasterDataCrudPage<T = any>({ schema }: MasterDataCrudPageProps<T>) {
  const { userRole } = useAuthStore();
  const queryClient = useQueryClient();
  const idField = schema.idField || 'id';
  // Entities outside the default /api/v1 axios instance supply their own client
  // (e.g. JobFamily/PayGrade live under /api/v2/compensation).
  const http = schema.httpClient ?? apiClient;

  const canCreate = hasAllowedRole(userRole, schema.permissions.create);
  const canEdit = hasAllowedRole(userRole, schema.permissions.edit);
  const canDelete = hasAllowedRole(userRole, schema.permissions.delete);
  const showRowActions = canEdit || canDelete;

  const listQueryKey = useMemo(() => [schema.entityKey, 'list'], [schema.entityKey]);

  const {
    data: rows = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: listQueryKey,
    queryFn: async () => {
      const response = await http.get(schema.apiBasePath);
      return normalizeListResponse<T>(response.data);
    },
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<T | null>(null);
  // BOT-004(a)-class hardening (issue #715, proactive audit — không phải
  // repro gốc): trước bản sửa này create/update/delete KHÔNG có onError và
  // KHÔNG nơi nào render `.isError`/`.error` của 3 mutation này — modal chỉ
  // đóng khi THÀNH CÔNG (onSuccess), nên khi API lỗi modal đứng im, nút Lưu
  // hết loading, KHÔNG có thông báo nào khác. Đây là generic CRUD framework
  // (EmployeeManagement, CompensationMasterData/JobFamilies+PayGrades đều
  // dùng chung component này) nên bug này có mặt ở MỌI trang dùng nó.
  const [mutationError, setMutationError] = useState<string | null>(null);

  const extractMutationError = useCallback((err: unknown): string => {
    const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      return detail.map((d) => (d as { msg?: unknown })?.msg ?? String(d)).join(', ');
    }
    return (err as { message?: string })?.message || 'Thao tác không thành công. Vui lòng thử lại.';
  }, []);

  const invalidateList = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: listQueryKey });
  }, [queryClient, listQueryKey]);

  const createMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) => http.post(schema.apiBasePath, payload),
    onSuccess: () => {
      setMutationError(null);
      invalidateList();
      setIsModalOpen(false);
      setEditingRow(null);
    },
    onError: (err) => setMutationError(extractMutationError(err)),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Record<string, unknown> }) =>
      http.put(`${schema.apiBasePath}/${id}`, payload),
    onSuccess: () => {
      setMutationError(null);
      invalidateList();
      setIsModalOpen(false);
      setEditingRow(null);
    },
    onError: (err) => setMutationError(extractMutationError(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => http.delete(`${schema.apiBasePath}/${id}`),
    onSuccess: () => {
      setMutationError(null);
      invalidateList();
    },
    onError: (err) => setMutationError(extractMutationError(err)),
  });

  const handleOpenCreate = useCallback(() => {
    setEditingRow(null);
    setMutationError(null);
    setIsModalOpen(true);
  }, []);

  const handleOpenEdit = useCallback((row: T) => {
    setEditingRow(row);
    setMutationError(null);
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingRow(null);
    setMutationError(null);
  }, []);

  const handleDelete = useCallback(
    (row: T) => {
      const id = getRowId(row, idField);
      if (!id) return;
      // eslint-disable-next-line no-alert
      if (window.confirm(`Bạn có chắc chắn muốn xóa ${schema.entityLabel.toLowerCase()} này?`)) {
        deleteMutation.mutate(id);
      }
    },
    [deleteMutation, idField, schema.entityLabel]
  );

  const handleSubmitForm = useCallback(
    (payload: Record<string, unknown>) => {
      if (editingRow) {
        const id = getRowId(editingRow, idField);
        updateMutation.mutate({ id, payload });
      } else {
        createMutation.mutate(payload);
      }
    },
    [editingRow, idField, createMutation, updateMutation]
  );

  // ─── Import / Export (BR-PLAT-001-R2/R6) ─────────────────────────────────
  // These were TODO stubs until JobFamily/PayGrade (BR-COMP-002 §8 R2) became
  // the framework's first real caller. They are deliberately generic: every
  // entity gets working Excel import/export by declaring
  // `importExport: { enabled: true, ... }` plus the two backend endpoints.
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [importResult, setImportResult] = useState<ImportResultSummary | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const importPath = schema.importExport?.importPath ?? `${schema.apiBasePath}/import`;
  const exportPath = schema.importExport?.exportPath ?? `${schema.apiBasePath}/export`;
  const importMode = schema.importExport?.mode ?? 'upsert';

  const handleImportClick = useCallback(() => {
    setImportError(null);
    setImportResult(null);
    fileInputRef.current?.click();
  }, []);

  const handleFileSelected = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      // Reset immediately so re-selecting the SAME file still fires onChange.
      event.target.value = '';
      if (!file) return;

      setIsImporting(true);
      setImportError(null);
      setImportResult(null);
      try {
        const formData = new FormData();
        formData.append('file', file);
        const response = await http.post(
          `${importPath}?mode=${importMode}&dry_run=false`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        setImportResult(response.data as ImportResultSummary);
        invalidateList();
      } catch (err: any) {
        setImportError(
          err?.response?.data?.detail || err?.message || 'Nhập Excel thất bại'
        );
      } finally {
        setIsImporting(false);
      }
    },
    [http, importPath, importMode, invalidateList]
  );

  const handleExportClick = useCallback(async () => {
    setIsExporting(true);
    setImportError(null);
    try {
      const response = await http.get(exportPath, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        schema.importExport?.exportFileName ?? `${schema.entityKey}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setImportError(err?.message || 'Xuất Excel thất bại');
    } finally {
      setIsExporting(false);
    }
  }, [http, exportPath, schema.importExport, schema.entityKey]);

  const columns: ColumnDef<T>[] = useMemo(() => {
    if (!showRowActions) return schema.columns;
    const actionsColumn: ColumnDef<T> = {
      key: '__actions',
      label: 'Hành động',
      align: 'center',
      width: '110px',
      render: (_value, row) => (
        <div className={styles.rowActions}>
          {canEdit && (
            <button
              type="button"
              className={styles.rowActionBtn}
              title="Chỉnh sửa"
              aria-label={`Chỉnh sửa ${schema.entityLabel}`}
              onClick={() => handleOpenEdit(row)}
            >
              ✏️
            </button>
          )}
          {canDelete && (
            <button
              type="button"
              className={styles.rowActionBtnDanger}
              title="Xóa"
              aria-label={`Xóa ${schema.entityLabel}`}
              onClick={() => handleDelete(row)}
            >
              🗑️
            </button>
          )}
        </div>
      ),
    };
    return [...schema.columns, actionsColumn];
  }, [schema.columns, schema.entityLabel, showRowActions, canEdit, canDelete, handleOpenEdit, handleDelete]);

  const importExportEnabled = !!schema.importExport?.enabled;

  return (
    <div className={styles.page} data-testid="master-data-crud-page">
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>{schema.entityLabel}</h1>
          <p className={styles.subtitle}>Quản lý danh mục {schema.entityLabel.toLowerCase()}</p>
        </div>
      </div>

      {isError && (
        <div className={styles.errorBanner} role="alert">
          <span>⚠️ {(error as Error)?.message || 'Đã xảy ra lỗi khi tải dữ liệu'}</span>
          <button type="button" className={styles.btnSecondary} onClick={() => refetch()}>
            Thử lại
          </button>
        </div>
      )}

      <div className={styles.actionBar}>
        <div className={styles.actionButtons}>
          {canCreate && (
            <button type="button" className={styles.btnPrimary} onClick={handleOpenCreate} data-testid="create-btn">
              <span>+</span>
              <span>Thêm {schema.entityLabel}</span>
            </button>
          )}
          {importExportEnabled && (
            <>
              {!schema.importExport?.exportOnly && (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.csv"
                    style={{ display: 'none' }}
                    onChange={handleFileSelected}
                    data-testid="import-file-input"
                  />
                  <button
                    type="button"
                    className={styles.btnSecondary}
                    onClick={handleImportClick}
                    disabled={isImporting}
                    data-testid="import-btn"
                  >
                    <span>📥</span>
                    <span>{isImporting ? 'Đang nhập...' : 'Nhập Excel'}</span>
                  </button>
                </>
              )}
              <button
                type="button"
                className={styles.btnSecondary}
                onClick={handleExportClick}
                disabled={isExporting}
                data-testid="export-btn"
              >
                <span>📤</span>
                <span>{isExporting ? 'Đang xuất...' : 'Xuất Excel'}</span>
              </button>
            </>
          )}
          <button
            type="button"
            className={styles.btnSecondary}
            onClick={() => refetch()}
            disabled={isLoading}
            data-testid="refresh-btn"
          >
            <span>{isLoading ? '⏳' : '🔄'}</span>
            <span>Làm mới</span>
          </button>
        </div>
      </div>

      {importError && (
        <div className={styles.errorBanner} role="alert" data-testid="import-error">
          <span>⚠️ {importError}</span>
        </div>
      )}

      {importResult && (
        // BR-EMP-002 R5 / BR-COMP-002 R4 partial-failure report: the import does
        // NOT stop at the first bad row, so the summary must show what landed
        // AND enumerate the rows that didn't, with their row numbers.
        <div className={styles.errorBanner} role="status" data-testid="import-result">
          <div>
            <strong>Kết quả nhập:</strong> tạo mới {importResult.created_count}, cập nhật{' '}
            {importResult.updated_count}, bỏ qua {importResult.skipped_count}, lỗi{' '}
            {importResult.failed_count}
            {importResult.errors?.length > 0 && (
              <ul>
                {importResult.errors.slice(0, 10).map((rowError, idx) => (
                  <li key={`${rowError.row_number}-${rowError.field ?? 'row'}-${idx}`}>
                    Dòng {rowError.row_number}
                    {rowError.field ? ` — ${rowError.field}` : ''}: {rowError.message}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      <DataTable<T>
        data={rows}
        columns={columns}
        isLoading={isLoading}
        emptyMessage={`Chưa có ${schema.entityLabel.toLowerCase()} nào`}
      />

      {/* Lỗi xoá (deleteMutation) không có modal riêng để hiện — banner ở
          đây dùng chung cho cả create/update/delete (#715 hardening). */}
      {mutationError && !isModalOpen && (
        <div className={styles.errorBanner} role="alert" data-testid="mutation-error">
          <span>⚠️ {mutationError}</span>
        </div>
      )}

      {isModalOpen && (
        <EntityFormModal
          entityLabel={schema.entityLabel}
          fields={schema.formFields}
          initialRow={editingRow}
          isSubmitting={createMutation.isPending || updateMutation.isPending}
          submitError={mutationError}
          onClose={handleCloseModal}
          onSubmit={handleSubmitForm}
        />
      )}
    </div>
  );
}

// ─── Generic create/edit form modal ────────────────────────────────────────

interface EntityFormModalProps {
  entityLabel: string;
  fields: FieldDef[];
  initialRow: unknown;
  isSubmitting: boolean;
  /** #715 hardening: message từ create/update mutation onError (parent state) */
  submitError: string | null;
  onClose: () => void;
  onSubmit: (payload: Record<string, unknown>) => void;
}

function buildInitialFormState(fields: FieldDef[], initialRow: unknown): Record<string, unknown> {
  const state: Record<string, unknown> = {};
  const row = (initialRow || {}) as Record<string, unknown>;
  fields.forEach((field) => {
    if (initialRow) {
      state[field.key] = row[field.key] ?? (field.type === 'boolean' ? false : '');
    } else {
      state[field.key] = field.defaultValue ?? (field.type === 'boolean' ? false : '');
    }
  });
  return state;
}

const EntityFormModal: React.FC<EntityFormModalProps> = ({
  entityLabel,
  fields,
  initialRow,
  isSubmitting,
  submitError,
  onClose,
  onSubmit,
}) => {
  const isEditMode = !!initialRow;
  const [formState, setFormState] = useState<Record<string, unknown>>(() =>
    buildInitialFormState(fields, initialRow)
  );
  const [errors, setErrors] = useState<Record<string, string>>({});

  const setFieldValue = useCallback((key: string, value: unknown) => {
    setFormState((prev) => ({ ...prev, [key]: value }));
  }, []);

  const validateAll = useCallback((): boolean => {
    const nextErrors: Record<string, string> = {};
    fields.forEach((field) => {
      const value = formState[field.key];
      if (field.required && (value === '' || value === null || value === undefined)) {
        nextErrors[field.key] = `${field.label} là bắt buộc`;
        return;
      }
      if (field.validate) {
        const message = field.validate(value);
        if (message) {
          nextErrors[field.key] = message;
        }
      }
    });
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }, [fields, formState]);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (!validateAll()) return;
      onSubmit(formState);
    },
    [validateAll, formState, onSubmit]
  );

  return (
    <div className={styles.modalOverlay} role="dialog" aria-modal="true">
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>
            {isEditMode ? `Chỉnh sửa ${entityLabel}` : `Thêm ${entityLabel} mới`}
          </h2>
          <button type="button" className={styles.modalCloseBtn} onClick={onClose} aria-label="Đóng">
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className={styles.modalBody}>
            {/* #715 hardening: trước đây create/update lỗi (network/422/500)
                không hiện gì — modal đứng im, nút Lưu hết loading, không có
                thông báo nào. */}
            {submitError && (
              <div className={styles.errorBanner} role="alert" data-testid="submit-error">
                <span>⚠️ {submitError}</span>
              </div>
            )}
            {fields.map((field) => (
              <FormFieldInput
                key={field.key}
                field={field}
                value={formState[field.key]}
                error={errors[field.key]}
                onChange={(value) => setFieldValue(field.key, value)}
              />
            ))}
          </div>
          <div className={styles.modalFooter}>
            <button type="button" className={styles.btnSecondary} onClick={onClose}>
              Hủy
            </button>
            <button type="submit" className={styles.btnPrimary} disabled={isSubmitting} data-testid="submit-btn">
              {isSubmitting ? 'Đang lưu...' : 'Lưu'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

interface FormFieldInputProps {
  field: FieldDef;
  value: unknown;
  error?: string;
  onChange: (value: unknown) => void;
}

const FormFieldInput: React.FC<FormFieldInputProps> = ({ field, value, error, onChange }) => {
  const inputClassName = error ? styles.formInputError : styles.formInput;

  return (
    <div className={styles.formField}>
      <label className={styles.formLabel} htmlFor={`field-${field.key}`}>
        {field.label}
        {field.required && <span className={styles.formRequired}>*</span>}
      </label>

      {field.type === 'boolean' ? (
        <div className={styles.formCheckboxRow}>
          <input
            id={`field-${field.key}`}
            type="checkbox"
            checked={!!value}
            onChange={(e) => onChange(e.target.checked)}
          />
        </div>
      ) : field.type === 'select' ? (
        <select
          id={`field-${field.key}`}
          className={inputClassName}
          value={value as string | number | undefined}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">— Chọn —</option>
          {(field.options || []).map((opt) => (
            <option key={String(opt.value)} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      ) : field.type === 'textarea' ? (
        <textarea
          id={`field-${field.key}`}
          className={`${inputClassName} ${styles.formTextarea}`}
          placeholder={field.placeholder}
          rows={field.rows ?? 4}
          // DD-0080 §3 sanctions textarea-as-JSON for narrow JSONB fields. In EDIT
          // mode the row's value arrives from the API as a parsed object, which
          // would otherwise render as the literal string "[object Object]" —
          // serialize it back to JSON text so the round-trip actually works.
          value={
            value !== null && typeof value === 'object'
              ? JSON.stringify(value, null, 2)
              : ((value as string | undefined) ?? '')
          }
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <input
          id={`field-${field.key}`}
          className={inputClassName}
          type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
          placeholder={field.placeholder}
          value={value as string | number | undefined}
          onChange={(e) =>
            onChange(field.type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value)
          }
        />
      )}

      {field.helpText && !error && <span className={styles.formHelpText}>{field.helpText}</span>}
      {error && <span className={styles.formErrorText}>{error}</span>}
    </div>
  );
};

export default MasterDataCrudPage;
